import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserMinusIcon, AlertTriangleIcon, CheckIcon, XIcon, Loader2Icon, ShieldIcon, DatabaseIcon, UsersIcon } from 'lucide-react';
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  profile: string;
  status: string;
}
const MOCK_USERS: User[] = [{
  id: '1',
  firstName: 'John',
  lastName: 'Martinez',
  email: 'john.martinez@truss.com',
  role: 'Jr. Loan Officer',
  profile: 'Loan Officer Profile',
  status: 'Active'
}, {
  id: '2',
  firstName: 'Sarah',
  lastName: 'Chen',
  email: 'sarah.chen@truss.com',
  role: 'Processor',
  profile: 'Processor',
  status: 'Active'
}, {
  id: '3',
  firstName: 'Mike',
  lastName: 'Johnson',
  email: 'mike.johnson@truss.com',
  role: 'Jr. Loan Officer',
  profile: 'Loan Officer Profile II',
  status: 'Active'
}, {
  id: '4',
  firstName: 'Emily',
  lastName: 'Davis',
  email: 'emily.davis@truss.com',
  role: 'Processor Manager',
  profile: 'Processing Manager',
  status: 'Active'
}, {
  id: '5',
  firstName: 'David',
  lastName: 'Wilson',
  email: 'david.wilson@truss.com',
  role: 'Solution Desk',
  profile: 'Solution Desk',
  status: 'Active'
}, {
  id: '6',
  firstName: 'Lisa',
  lastName: 'Thompson',
  email: 'lisa.thompson@truss.com',
  role: 'Jr. Processor',
  profile: 'Junior Processor',
  status: 'Active'
}, {
  id: '7',
  firstName: 'James',
  lastName: 'Brown',
  email: 'james.brown@truss.com',
  role: 'Retail',
  profile: 'Sales Manager',
  status: 'Active'
}, {
  id: '8',
  firstName: 'Amanda',
  lastName: 'Garcia',
  email: 'amanda.garcia@truss.com',
  role: 'Upsell',
  profile: 'Upsell',
  status: 'Active'
}, {
  id: '9',
  firstName: 'Robert',
  lastName: 'Taylor',
  email: 'robert.taylor@truss.com',
  role: 'Jr. Loan Officer',
  profile: 'Loan Officer Profile',
  status: 'Active'
}, {
  id: '10',
  firstName: 'Jennifer',
  lastName: 'Lee',
  email: 'jennifer.lee@truss.com',
  role: 'Processor',
  profile: 'Processor Restricted',
  status: 'Active'
}, {
  id: '11',
  firstName: 'Chris',
  lastName: 'Anderson',
  email: 'chris.anderson@truss.com',
  role: 'Solution Desk',
  profile: 'Solution Desk',
  status: 'Active'
}, {
  id: '12',
  firstName: 'Maria',
  lastName: 'Rodriguez',
  email: 'maria.rodriguez@truss.com',
  role: 'Jr. Loan Officer',
  profile: 'Loan Officer Profile',
  status: 'Active'
}];
const AVATAR_COLORS = ['bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700', 'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-indigo-100 text-indigo-700'];
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
interface OffboardingPanelProps {
  onStepChange?: (step: number) => void;
}
export function OffboardingPanel({
  onStepChange
}: OffboardingPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [deactivatedUsers, setDeactivatedUsers] = useState<User[]>([]);
  // Communicate current step to parent
  useEffect(() => {
    if (isSuccess) {
      onStepChange?.(2);
    } else if (showConfirmModal) {
      onStepChange?.(1);
    } else {
      onStepChange?.(0);
    }
  }, [isSuccess, showConfirmModal, onStepChange]);
  // Filter out users that have been deactivated in this session
  const activeUsers = useMemo(() => {
    const deactivatedIds = new Set(deactivatedUsers.map(u => u.id));
    return MOCK_USERS.filter(u => !deactivatedIds.has(u.id));
  }, [deactivatedUsers]);
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return activeUsers.filter(user => user.firstName.toLowerCase().includes(query) || user.lastName.toLowerCase().includes(query) || user.email.toLowerCase().includes(query) || user.role.toLowerCase().includes(query));
  }, [searchQuery, activeUsers]);
  const selectedUsers = useMemo(() => {
    return activeUsers.filter(user => selectedUserIds.has(user.id));
  }, [selectedUserIds, activeUsers]);
  const toggleUser = (id: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedUserIds(newSelected);
  };
  const handleDeactivateClick = () => {
    if (selectedUserIds.size > 0) {
      setShowConfirmModal(true);
    }
  };
  const confirmDeactivate = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsSuccess(true);
      setShowConfirmModal(false);
      setDeactivatedUsers(prev => [...prev, ...selectedUsers]);
    }, 1500);
  };
  const resetFlow = () => {
    setIsSuccess(false);
    setSelectedUserIds(new Set());
    setSearchQuery('');
  };
  if (isSuccess) {
    return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center min-h-[600px]" data-id="element-15">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-16">
          <CheckIcon className="w-10 h-10 text-white" data-id="element-17" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-2xl font-bold text-gray-900 mb-2" data-id="element-18">
          Users Successfully Deactivated
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-sm text-gray-600 mb-10" data-id="element-19">
          {selectedUsers.length} user(s) have been deactivated and their access
          has been revoked.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 text-left w-full max-w-3xl" data-id="element-20">
          {[{
          icon: UserMinusIcon,
          count: selectedUsers.length,
          label: 'Users Deactivated'
        }, {
          icon: ShieldIcon,
          count: selectedUsers.length * 3,
          label: 'Licenses Revoked'
        }, {
          icon: UsersIcon,
          count: selectedUsers.length * 2,
          label: 'Groups Removed'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center space-x-4 shadow-sm" data-id="element-21">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-22">
                <item.icon className="w-5 h-5 text-red-600" data-id="element-23" />
              </div>
              <div data-id="element-24">
                <div className="text-xl font-bold text-gray-900 leading-none" data-id="element-25">
                  {item.count}
                </div>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1" data-id="element-26">
                  {item.label}
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6
      }} onClick={resetFlow} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2563EB] transition-colors" data-id="element-27">
          Start New Offboarding
        </motion.button>
      </div>;
  }
  return <div className="flex flex-col h-full relative" data-id="element-28">
      {/* Top Section */}
      <div className="p-6 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-29">
        <div className="mb-6" data-id="element-30">
          <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-31">Offboarding</h1>
          <p className="text-sm text-gray-500" data-id="element-32">
            Search and select users to deactivate their accounts and revoke
            access.
          </p>
        </div>

        <div className="relative" data-id="element-33">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-34">
            <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-35" />
          </div>
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] sm:text-sm transition-colors" placeholder="Search users by name, email, or role..." data-id="element-36" />
        </div>

        {/* Selected Users Bar */}
        <AnimatePresence data-id="element-37">
          {selectedUserIds.size > 0 && <motion.div initial={{
          opacity: 0,
          height: 0,
          marginTop: 0
        }} animate={{
          opacity: 1,
          height: 'auto',
          marginTop: 16
        }} exit={{
          opacity: 0,
          height: 0,
          marginTop: 0
        }} className="bg-blue-50 border border-blue-100 rounded-lg p-3 overflow-hidden" data-id="element-38">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-39">
                <span className="text-sm font-medium text-blue-800 flex-shrink-0" data-id="element-40">
                  {selectedUserIds.size} user(s) selected:
                </span>
                <div className="flex flex-wrap gap-2" data-id="element-41">
                  {selectedUsers.map(user => <div key={user.id} className="inline-flex items-center bg-white border border-blue-200 rounded-full px-2 py-1 shadow-sm" data-id="element-42">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mr-1.5 ${getAvatarColor(user.firstName)}`} data-id="element-43">
                        {user.firstName[0]}
                        {user.lastName[0]}
                      </div>
                      <span className="text-xs font-medium text-gray-700 mr-1" data-id="element-44">
                        {user.firstName}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  toggleUser(user.id);
                }} className="text-gray-400 hover:text-gray-600 focus:outline-none" data-id="element-45">
                        <XIcon className="w-3.5 h-3.5" data-id="element-46" />
                      </button>
                    </div>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-y-auto p-6 pb-24" data-id="element-47">
        {filteredUsers.length === 0 ? <div className="text-center py-12" data-id="element-48">
            <p className="text-sm text-gray-500" data-id="element-49">
              No active users found matching "{searchQuery}"
            </p>
          </div> : <div className="space-y-1" data-id="element-50">
            {filteredUsers.map(user => {
          const isSelected = selectedUserIds.has(user.id);
          return <div key={user.id} onClick={() => toggleUser(user.id)} className={`flex items-center p-3 rounded-lg border-b border-transparent cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-blue-100' : 'hover:bg-gray-50 border-gray-50'}`} data-id="element-51">
                  <div className="flex-shrink-0 mr-4" data-id="element-52">
                    <input type="checkbox" checked={isSelected} readOnly className="h-4 w-4 text-[#2563EB] rounded border-gray-300 focus:ring-[#2563EB] pointer-events-none" data-id="element-53" />
                  </div>
                  <div className="flex-shrink-0 mr-4" data-id="element-54">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getAvatarColor(user.firstName)}`} data-id="element-55">
                      {user.firstName[0]}
                      {user.lastName[0]}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-56">
                    <div className="flex items-center justify-between" data-id="element-57">
                      <p className="text-sm font-bold text-gray-900 truncate" data-id="element-58">
                        {user.firstName} {user.lastName}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800" data-id="element-59">
                        {user.status}
                      </span>
                    </div>
                    <div className="flex items-center mt-0.5" data-id="element-60">
                      <p className="text-xs text-gray-500 truncate mr-3" data-id="element-61">
                        {user.email}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600" data-id="element-62">
                        {user.role}
                      </span>
                    </div>
                  </div>
                </div>;
        })}
          </div>}
      </div>

      {/* Bottom Action Bar */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white flex items-center justify-between" data-id="element-63">
        <span className="text-sm font-medium text-gray-600" data-id="element-64">
          {selectedUserIds.size} of {activeUsers.length} users selected
        </span>
        <button onClick={handleDeactivateClick} disabled={selectedUserIds.size === 0} className={`flex items-center justify-center px-6 py-2.5 text-sm font-medium rounded-full shadow-sm transition-all ${selectedUserIds.size > 0 ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-65">
          <UserMinusIcon className="w-4 h-4 mr-2" data-id="element-66" />
          Deactivate Selected
        </button>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence data-id="element-67">
        {showConfirmModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-gray-900/50 z-40 rounded-xl" onClick={() => !isProcessing && setShowConfirmModal(false)} data-id="element-68" />
            <div className="absolute inset-0 flex items-center justify-center z-50 p-4 pointer-events-none" data-id="element-69">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 10
          }} className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden pointer-events-auto flex flex-col max-h-full" data-id="element-70">
                <div className="p-6 border-b border-gray-100 flex-shrink-0" data-id="element-71">
                  <div className="flex items-center space-x-3 text-amber-600 mb-4" data-id="element-72">
                    <AlertTriangleIcon className="w-6 h-6" data-id="element-73" />
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-74">
                      Confirm Deactivation
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-75">
                    You are about to deactivate{' '}
                    <span className="font-bold text-gray-900" data-id="element-76">
                      {selectedUserIds.size} user(s)
                    </span>
                    . This will:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside mb-4" data-id="element-77">
                    <li data-id="element-78">Remove their Salesforce access</li>
                    <li data-id="element-79">Revoke all package licenses</li>
                    <li data-id="element-80">Remove from public groups</li>
                    <li data-id="element-81">Freeze their login credentials</li>
                  </ul>
                </div>

                <div className="p-6 bg-gray-50 overflow-y-auto flex-1" data-id="element-82">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-83">
                    Users to deactivate
                  </h4>
                  <div className="space-y-2" data-id="element-84">
                    {selectedUsers.map(user => <div key={user.id} className="flex items-center justify-between text-sm" data-id="element-85">
                        <span className="font-medium text-gray-900" data-id="element-86">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="text-gray-500 truncate ml-4" data-id="element-87">
                          {user.email}
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="p-4 border-t border-gray-100 bg-white flex justify-end space-x-3 flex-shrink-0" data-id="element-88">
                  <button onClick={() => setShowConfirmModal(false)} disabled={isProcessing} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2563EB] disabled:opacity-50" data-id="element-89">
                    Cancel
                  </button>
                  <button onClick={confirmDeactivate} disabled={isProcessing} className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 disabled:opacity-70 min-w-[140px]" data-id="element-90">
                    {isProcessing ? <>
                        <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-91" />
                        Processing...
                      </> : 'Deactivate Users'}
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}